import { apiFetch } from "../client"

export interface PortalLoanCard {
  loanId: string
  productName: string
  loanType: string
  microLoanTier: string | null
  principalAmount: number
  applicationFeeAmount: number
  netDisbursedAmount: number | null
  currency: string
  disbursementDate: string | null
  dueDate: string | null
  outstandingBalance: number
  totalPenalties: number
  totalRepaid: number
  totalRepayableAmount: number | null
  repaymentProgressPercent: number
  overdueLabel: string
}

export interface PortalLoanDetail {
  loanId: string
  productName: string
  loanType: string
  microLoanTier: string | null
  principalAmount: number
  interestRate: number
  applicationFeeAmount: number
  netDisbursedAmount: number | null
  currency: string
  totalRepayableAmount: number
  disbursementDate: string | null
  dueDate: string | null
  outstandingBalance: number
  totalPenalties: number
  totalRepaid: number
  overdueLabel: string
  repaymentSchedule: any[]
  repaymentHistory: any[]
  penaltyBreakdown: any[]
}

export interface PortalMicroLoanEligibility {
  client: {
    clientId: string
    fullName: string
    phoneNumber: string
  }
  eligible: boolean
  reasons: string[]
  tier: {
    currentTier: string
    tierName: string
    tierScore: number
    isFrozen: boolean
    freezeReason: string | null
    terms: {
      principalCeiling: number
      principalCeilingDisplay: string
      minTenorDays: number
      maxTenorDays: number
      defaultTenorDays: number
      applicationFeeRate: number
      interestRate: number
      requiredCycles: number
    }
    nextTier: {
      tier: string
      tierName: string | null
      remainingCycles: number
      principalCeiling: number | null
      principalCeilingDisplay: string | null
    } | null
  }
  activeMicroLoan: {
    loanId: string
    principalAmount: number
    applicationFeeAmount: number
    netDisbursedAmount: number | null
    interestRate: number
    termDays: number
    lifecycleStage: string
    overdueCategory: string | null
    dueDate: string | null
    disbursementDate: string | null
    closedAt: string | null
    microLoanTier: string | null
  } | null
}

export interface PortalPaymentRow {
  paymentId: string
  loanId: string
  amount: number
  currency: string
  paymentMethod: string
  transactionReference: string | null
  status: string
  rejectionReason: string | null
  paymentDate: string
  createdAt: string
}

export interface PortalNotificationRow {
  notificationId: string
  eventType: string | null
  notificationKey: string | null
  title: string
  body: string
  deepLink: string | null
  isRead: boolean
  readAt: string | null
  createdAt: string
}

export interface PortalCurrencyAmount {
  currency: string
  amount: number
}

export interface PortalProfile {
  clientId: string
  fullName: string
  dateOfBirth: string | null
  phoneNumber: string
  altPhone: string | null
  physicalAddress: string | null
  preferredLanguage: string
  kycStatus: string
  kycRejectionReason: string | null
  webPushPublicKey: string
  documents: Array<{
    documentId: string
    documentType: string
    fileName: string
    mimeType: string
    createdAt: string
    downloadPath: string
  }>
  applicationSummary?: PortalApplication | null
}

export interface PortalKycRequestItem {
  requestItemId: string
  itemKind: string
  itemKey: string
  label: string
  instructions: string
  responseStatus: string
  responseValue: string | null
  respondedAt: string | null
  createdAt: string
}

export interface PortalKycRequest {
  requestId: string
  message: string
  createdAt: string
  respondedAt: string | null
  items: PortalKycRequestItem[]
}

export interface PortalKycDocument {
  documentId: string
  documentType: string
  fileName: string
  mimeType: string
  createdAt: string
  downloadPath: string
}

export interface PortalKycDocumentGroup {
  documentType: string
  latestDocument: PortalKycDocument | null
  history: PortalKycDocument[]
  totalUploads: number
}

export interface PortalLoanProduct {
  productId: string
  name: string
  description: string | null
  principalMin: number
  principalMax: number
  interestRate: number
  termDays: number
  currency: string
}

export interface PortalApplication {
  applicationId: string
  product: {
    productId: string
    name: string
  }
  requestedPrincipalAmount: number
  paymentMethod: string
  destinationAccount: string
  status: string
  rejectionReason: string | null
  kycStatus: string
  kycRejectionReason: string | null
  submittedAt: string | null
  createdAt: string
  updatedAt: string
  convertedLoanId: string | null
}

export interface PortalOnboarding {
  clientId: string
  fullName: string
  dateOfBirth: string | null
  gender: string | null
  phoneNumber: string
  altPhone: string | null
  physicalAddress: string | null
  preferredLanguage: "en" | "sn"
  kycStatus: string
  kycRejectionReason: string | null
  nationalId: string | null
  nextOfKin: {
    id?: string
    fullName: string
    relationship: string
    phoneNumber: string
  } | null
  documents: PortalKycDocument[]
  documentGroups: PortalKycDocumentGroup[]
  application: PortalApplication | null
  activeKycRequest: PortalKycRequest | null
  requiredDocumentTypes: string[]
  optionalDocumentTypes: string[]
  missingDocumentTypes: string[]
  hasRequiredDocuments: boolean
  isProfileComplete: boolean
  portalStandardProductsEnabled: boolean
}

export async function getDashboard() {
  return apiFetch<{
    status: string
    data: {
      summary: {
        totalActiveLoans: number
        totalOutstandingBalanceByCurrency: PortalCurrencyAmount[]
        paymentStreak: number
      }
      loans: PortalLoanCard[]
      nextPayment: {
        loanId: string
        amount: number
        currency: string
        dueDate: string | null
      } | null
      recentActivity: PortalPaymentRow[]
    }
  }>("/portal/dashboard")
}

export async function listLoans(
  page = 1,
  perPage = 20,
  options?: {
    activeOnly?: boolean
  },
) {
  const query = new URLSearchParams()
  query.set("page", String(page))
  query.set("per_page", String(perPage))
  if (options?.activeOnly) {
    query.set("active_only", "true")
  }

  return apiFetch<{
    status: string
    data: PortalLoanCard[]
    meta: {
      page: number
      perPage: number
      totalItems: number
      totalPages: number
    }
  }>(`/portal/loans?${query.toString()}`)
}

export async function getLoan(loanId: string) {
  return apiFetch<{ status: string; data: PortalLoanDetail }>(`/portal/loans/${loanId}`)
}

export async function getLoanRepayments(loanId: string) {
  return apiFetch<any>(`/portal/loans/${loanId}/repayments`)
}

export async function listPayments(params: {
  pageParam?: number
  loanId?: string
  search?: string
  status?: string
}) {
  const query = new URLSearchParams()
  query.set("page", String(params.pageParam ?? 1))
  query.set("per_page", "10")
  if (params.loanId) query.set("loan_id", params.loanId)
  if (params.search) query.set("search", params.search)
  if (params.status) query.set("status", params.status)

  return apiFetch<{
    status: string
    data: PortalPaymentRow[]
    meta: {
      page: number
      perPage: number
      totalItems: number
      totalPages: number
    }
  }>(`/portal/payments?${query.toString()}`)
}

export async function initiatePayment(loanId: string, amount: number) {
  return apiFetch<{
    status: string
    data: {
      paymentId: string
      loanId: string
      amount: number
      currency: string
      paymentMethod: string
      provider: string
      providerReference: string | null
      status: string
      failureReason: string | null
      initiatedAt: string
      completedAt: string | null
      repaymentId: string | null
      timeoutSeconds: number
    }
  }>("/portal/payments/initiate", {
    method: "POST",
    rawBody: { loanId, amount, paymentMethod: "ECOCASH" },
  })
}

export async function initiatePaymentWithMethod(
  loanId: string,
  amount: number,
  paymentMethod: "ECOCASH" | "INNBUCKS",
) {
  return apiFetch<{
    status: string
    data: {
      paymentId: string
      loanId: string
      amount: number
      currency: string
      paymentMethod: string
      provider: string
      providerReference: string | null
      status: string
      failureReason: string | null
      initiatedAt: string
      completedAt: string | null
      repaymentId: string | null
      timeoutSeconds: number
    }
  }>("/portal/payments/initiate", {
    method: "POST",
    rawBody: { loanId, amount, paymentMethod },
  })
}

export async function confirmManualPayment(body: {
  loanId: string
  transactionReference: string
  amount: number
  paymentDate: string
  paymentMethod: "BANK_TRANSFER" | "CASH_AT_AGENT" | "OTHER"
}) {
  return apiFetch<{ status: string; data: PortalPaymentRow }>("/portal/payments/confirm-manual", {
    method: "POST",
    rawBody: body,
  })
}

export async function getPaymentStatus(paymentId: string) {
  return apiFetch<{
    status: string
    data: {
      paymentId: string
      loanId: string
      amount: number
      currency: string
      paymentMethod: string
      provider: string
      providerReference: string | null
      status: string
      failureReason: string | null
      initiatedAt: string
      completedAt: string | null
      repaymentId: string | null
      timeoutSeconds: number
    }
  }>(`/portal/payments/${paymentId}/status`)
}

export async function getProfile() {
  return apiFetch<{ status: string; data: PortalProfile }>("/portal/profile")
}

export async function updateProfile(body: {
  altPhone?: string | null
  physicalAddress?: string | null
  preferredLanguage?: "en" | "sn"
}) {
  return apiFetch<{ status: string; data: PortalProfile }>("/portal/profile", {
    method: "PATCH",
    rawBody: body,
  })
}

export async function getOnboarding() {
  return apiFetch<{ status: string; data: PortalOnboarding }>("/portal/onboarding")
}

export async function updateOnboarding(body: {
  fullName?: string
  dateOfBirth?: string
  gender?: "MALE" | "FEMALE" | "OTHER"
  altPhone?: string | null
  physicalAddress?: string | null
  nationalId?: string
  preferredLanguage?: "en" | "sn"
  nextOfKin?: {
    fullName: string
    relationship: string
    phoneNumber: string
  }
}) {
  return apiFetch<{ status: string; data: PortalOnboarding }>("/portal/onboarding", {
    method: "PATCH",
    rawBody: body,
  })
}

export async function uploadPortalKycDocument(
  documentType: string,
  file: File,
  requestItemId?: string | null,
) {
  const form = new FormData()
  form.append("document_type", documentType)
  if (requestItemId) {
    form.append("request_item_id", requestItemId)
  }
  form.append("file", file)

  return apiFetch<{ status: string; data: PortalOnboarding }>("/portal/kyc-documents", {
    method: "POST",
    body: form,
  })
}

export async function resubmitPortalKyc() {
  return apiFetch<{ status: string; data: PortalOnboarding }>("/portal/kyc/resubmit", {
    method: "POST",
  })
}

export async function listLoanProducts() {
  return apiFetch<{ status: string; data: PortalLoanProduct[] }>("/portal/loan-products")
}

export async function getMicroLoanEligibility() {
  return apiFetch<{ status: string; data: PortalMicroLoanEligibility }>(
    "/portal/micro-loan/eligibility",
  )
}

export async function applyForMicroLoan(payload: {
  principalAmount: number
  tenorDays: number
  disbursementMethod: "ECOCASH" | "INNBUCKS"
  disbursementAccount: string
  portalDisclosureAccepted: true
  portalDisclosureAcceptedAt?: string
}) {
  return apiFetch<{
    status: string
    data: {
      loan: PortalLoanDetail
      manualReviewRequired: boolean
      pricing: {
        principalAmount: number
        applicationFeeAmount: number
        netDisbursedAmount: number
        totalRepayableAmount: number
        interestRate: number
        applicationFeeRate: number
        termDays: number
      }
    }
  }>("/portal/micro-loan/apply", {
    method: "POST",
    rawBody: payload,
  })
}

export async function getApplication() {
  return apiFetch<{ status: string; data: PortalApplication | null }>("/portal/application")
}

export async function createApplication(body: {
  productId: string
  requestedPrincipalAmount: number
  paymentMethod: string
  destinationAccount: string
  action: "SAVE_DRAFT" | "SUBMIT"
}) {
  return apiFetch<{ status: string; data: PortalApplication }>("/portal/application", {
    method: "POST",
    rawBody: body,
  })
}

export async function updateApplication(body: {
  productId?: string
  requestedPrincipalAmount?: number
  paymentMethod?: string
  destinationAccount?: string
  action: "SAVE_DRAFT" | "SUBMIT" | "WITHDRAW"
}) {
  return apiFetch<{ status: string; data: PortalApplication }>("/portal/application", {
    method: "PATCH",
    rawBody: body,
  })
}

export async function listPortalNotifications(page = 1, perPage = 10) {
  return apiFetch<{
    status: string
    data: PortalNotificationRow[]
    meta: {
      page: number
      perPage: number
      totalItems: number
      totalPages: number
      unreadCount: number
    }
  }>(`/portal/notifications?page=${page}&per_page=${perPage}`)
}

export async function markPortalNotificationRead(notificationId: string) {
  return apiFetch<{ status: string; data: { updated: boolean } }>(
    `/portal/notifications/${notificationId}/read`,
    {
      method: "PATCH",
    },
  )
}

export async function registerPushSubscription(body: {
  endpoint: string
  keys: { p256dh: string; auth: string }
  contentEncoding?: string
}) {
  return apiFetch<{ status: string; data: { registered: boolean } }>("/portal/push-subscriptions", {
    method: "POST",
    rawBody: body,
  })
}

export async function deletePushSubscription(endpoint: string) {
  return apiFetch<{ status: string; data: { deleted: boolean } }>("/portal/push-subscriptions", {
    method: "DELETE",
    rawBody: { endpoint },
  })
}

export async function getWebauthnRegisterOptions() {
  return apiFetch<{
    status: string
    data: { challengeId: string; options: Record<string, unknown> }
  }>("/portal/webauthn/register-options", {
    method: "POST",
  })
}

export async function registerWebauthnCredential(body: {
  challengeId: string
  response: Record<string, unknown>
}) {
  return apiFetch<{ status: string; data: { registered: boolean } }>("/portal/webauthn/register", {
    method: "POST",
    rawBody: body,
  })
}

export async function getWebauthnAuthOptions(clientId: string) {
  return apiFetch<{
    status: string
    data: {
      challengeId: string
      options: Record<string, unknown>
      client: { clientId: string; fullName: string; phoneNumber: string }
    }
  }>("/portal/webauthn/auth-options", {
    method: "POST",
    rawBody: { clientId },
  })
}

export async function authenticateWithWebauthn(body: {
  clientId: string
  challengeId: string
  response: Record<string, unknown>
}) {
  return apiFetch<{
    status: string
    data: {
      expiresAt: string
      client: {
        clientId: string
        fullName: string
        phoneNumber: string
        altPhone: string | null
        physicalAddress: string | null
        preferredLanguage: string
        kycStatus: string
      }
    }
  }>("/portal/webauthn/auth", {
    method: "POST",
    rawBody: body,
  })
}

export async function changePhone(body:
  | { step: "request_otp"; newPhoneNumber: string }
  | { step: "verify_otp"; changeRequestId: string; otp: string }) {
  return apiFetch<{ status: string; data: Record<string, unknown> }>("/portal/profile/change-phone", {
    method: "POST",
    rawBody: body,
  })
}

export async function listTickets(params: { page?: number; search?: string; status?: string }) {
  const query = new URLSearchParams()
  query.set("page", String(params.page ?? 1))
  query.set("per_page", "20")
  if (params.search) query.set("search", params.search)
  if (params.status) query.set("status", params.status)
  return apiFetch<any>(`/portal/tickets?${query.toString()}`)
}

export async function getTicket(ticketId: string) {
  return apiFetch<any>(`/portal/tickets/${ticketId}`)
}

export async function createTicket(body: {
  ticketType: "PAYMENT_ISSUE" | "ACCOUNT_ISSUE" | "DISPUTE" | "GENERAL_ENQUIRY"
  subject: string
  description: string
  loanId?: string
}) {
  return apiFetch<any>("/portal/tickets", {
    method: "POST",
    rawBody: body,
  })
}

export async function addTicketMessage(ticketId: string, body: string) {
  return apiFetch<any>(`/portal/tickets/${ticketId}/messages`, {
    method: "POST",
    rawBody: { body },
  })
}
